<template>
  <div class="main pages-registration-event-calendar">
    <Sidebar
      :current-keywords="keywords"
      :current-filters="currentFilters"
      :filter-types="filterTypes"
      @search="setKeywords"
      @add-filter="addFilter"
      @remove-filter="removeFilter"
      @remove-all-filters="removeAllCalendarFilters"
    >
      <div slot="header" class="sidebar-header">
        <div class="title-bar row no-gutters px-0 py-3 d-xl-none px-lg-3">
          <div class="col-12 text-center mb-3 col-lg-4 text-lg-left mb-lg-0">
            <h1>Event Calendar</h1>
          </div>
          <div class="col-12 col-lg-8">
            <Toggles :toggles="toggles" />
          </div>
        </div>
      </div>
      <div
        v-if="$root.isAdminager"
        slot="custom-filters"
        class="custom-filters"
      >
        <div class="inner pt-lg-3">
          <hr class="mt-0" />
          <Field
            type="radio"
            name="event_status"
            id="event_status_open"
            label="Upcoming Events"
            class="custom-control custom-radio mb-1"
            v-model="eventStatus"
            :radio-value="$root.constants.OPEN_EVENT"
          />
          <Field
            type="radio"
            name="event_status"
            id="event_status_pending"
            label="Pending Close"
            class="custom-control custom-radio mb-1"
            v-model="eventStatus"
            :radio-value="$root.constants.PENDING_CLOSE_EVENT"
          />
          <hr class="mt-0" />
          <Field
            v-if="$root.isTrainer"
            type="checkbox"
            name="trained_by_me"
            id="cb_trained_by_me"
            label="Trained By Me"
            v-model="trainedByMe"
            checkbox-value="1"
            class="custom-control custom-checkbox my-training-checkbox"
          />
          <Field
            v-if="$root.isLocalContact"
            type="checkbox"
            name="is_local_contact"
            id="cb_is_local_contact"
            label="Local Contact"
            v-model="isLocalContact"
            checkbox-value="1"
            class="custom-control custom-checkbox my-training-checkbox"
          />
        </div>
      </div>
    </Sidebar>

    <div class="title-bar row no-gutters p-3 d-none d-xl-flex">
      <div class="col-6">
        <h1>Event Calendar</h1>
      </div>
      <div class="col-6">
        <Toggles :toggles="toggles" />
      </div>
    </div>

    <div class="content p-3">
      <div class="row">
        <div class="col-12">
          <transition name="fade">
            <Alert v-if="hasAlert" :alert="alert" />
          </transition>

          <div v-if="showCalendar" class="row mb-5 d-lg-none">
            <div class="col-12">
              <div class="calendar-pagination text-center mb-3">
                <span class="prev" @click="prevMonth()">
                  <icon icon="arrow-left" class="svg-icon" />
                </span>
                <h3 class="d-inline text mx-3">
                  {{ month | moment("MMMM YYYY") }}
                </h3>
                <span class="next" @click="nextMonth()">
                  <icon icon="arrow-right" class="svg-icon" />
                </span>
              </div>
              <Calendar
                :month="month"
                :selected="day"
                :custom-classnames="calendarDayClasses"
                @day-selected="viewByDay"
              />
              <div v-if="!!day" class="text-center mt-3">
                <span class="btn btn-primary" @click="viewByDay(null)"
                  >View Full Month</span
                >
              </div>
            </div>
          </div>
          <div v-else class="row mb-3 d-lg-none">
            <div class="col-12 text-center">
              <span class="btn btn-primary" @click="viewCalendar()"
                >Show Calendar</span
              >
            </div>
          </div>

          <div class="row d-none d-lg-flex">
            <div class="col-4">
              <a
                v-if="items.length > 0 && $root.isLoggedIn"
                href="#"
                @click.prevent="showExportOptions()"
                class="btn btn-primary mr-1 mb-3"
              >
                <span class="icon icon-download"></span>
                <span class="text">Export List</span>
              </a>
              <a
                v-if="showCalendar"
                :href="route('event-calendar.print')"
                target="_blank"
                class="d-none d-xl-inline-block btn btn-primary mb-3"
              >
                <span class="icon icon-calendar"></span>
                <span class="text">Print Calendar</span>
              </a>
              <a
                v-else
                href="#"
                @click.prevent="viewCalendar()"
                class="btn btn-primary mb-3"
              >
                <span class="icon icon-calendar"></span>
                <span class="text">Show Calendar</span>
              </a>
            </div>
            <div v-if="!showCalendar" class="col-4 text-center">
              <Pagination
                class="d-inline-block"
                :total="totalPages"
                :value="parseInt(pagination.current)"
                @input="setCurrentPage"
              />
            </div>
            <div v-if="!showCalendar" class="col-4 text-right">
              <PageSize
                :value="parseInt(pagination.per_page)"
                @input="setPerPage"
              />
            </div>
          </div>

          <div class="row">
            <div
              :class="{
                'col-12': !showDetailsPane && !showCalendar,
                'col-8 details-pane-open': showDetailsPane || showCalendar,
              }"
            >
              <ListTable
                :items="events"
                :columns="columns"
                :activeColumns="activeColumns"
                :hideNoResults="hideNoResults"
                :hasDetailsPane="showDetailsPane || showCalendar"
                @sort-by="setSort"
                @swap-column="setActiveColumn"
                @item-clicked="setSelectedItem"
              >
                <template v-slot:col_start_date="{ item }">{{
                  item.startDate
                }}</template>
                <template v-slot:col_end_date="{ item }">{{
                  item.endDate
                }}</template>
                <template v-slot:col_city="{ item }">{{
                  item.city_and_state
                }}</template>
                <template v-slot:col_location="{ item }">{{
                  !!item.is_virtual ? "Virtual" : item.location
                }}</template>
                <template v-slot:col_price="{ item }">{{
                  item.enrollment_price | cost
                }}</template>
                <template v-slot:col_status="{ item }">{{
                  item.status_label
                }}</template>
                <template v-slot:list-card="{ item, id }">
                  <ListCard
                    :item="item"
                    :id="id"
                    :eventBus="listEvents"
                    section="registration"
                  />
                </template>
                <template v-slot:list-menu="{ item, id }">
                  <ListMenu
                    :item="item"
                    :id="id"
                    :eventBus="listEvents"
                    section="registration"
                  />
                </template>
              </ListTable>
            </div>
            <div v-if="showDetailsPane" class="col-4 details-pane-wrap mb-3">
              <EventDetailsPane
                :event="selectedItem"
                :tab="selectedTab"
                @close="setSelectedItem(null)"
              />
            </div>
            <div v-else-if="showCalendar" class="col-4 details-pane-wrap mb-3">
              <DetailsPane :auto-height="true" @close="hideCalendar()">
                <div slot="title" class="calendar-pagination mb-3">
                  <span class="prev" @click="prevMonth()">
                    <icon icon="arrow-left" class="svg-icon" />
                  </span>
                  <h3 class="d-inline text mx-2">
                    {{ month | moment("MMMM YYYY") }}
                  </h3>
                  <span class="next" @click="nextMonth()">
                    <icon icon="arrow-right" class="svg-icon" />
                  </span>
                </div>
                <Calendar
                  :month="month"
                  :selected="day"
                  :custom-classnames="calendarDayClasses"
                  @day-selected="viewByDay"
                />
                <div v-if="!!day" class="text-center my-3">
                  <span class="btn btn-primary" @click="viewByDay(null)"
                    >View Full Month</span
                  >
                </div>
              </DetailsPane>
            </div>
          </div>

          <div class="row">
            <div class="d-none d-lg-block col-4">
              <a
                v-if="items.length > 0 && $root.isLoggedIn"
                href="#"
                @click.prevent="showExportOptions()"
                class="btn btn-primary mr-1 mb-3"
              >
                <span class="icon icon-download"></span>
                <span class="text">Export List</span>
              </a>
              <a
                v-if="showCalendar"
                href="#"
                target="_blank"
                class="btn btn-primary mb-3"
              >
                <span class="icon icon-calendar"></span>
                <span class="text">Print Calendar</span>
              </a>
              <a
                v-else
                href="#"
                @click.prevent="viewCalendar()"
                class="btn btn-primary mb-3"
              >
                <span class="icon icon-calendar"></span>
                <span class="text">Show Calendar</span>
              </a>
            </div>
            <div
              v-if="!showCalendar"
              class="col-12 col-lg-4 mb-3 mb-lg-0 text-center"
            >
              <Pagination
                class="d-inline-block"
                :total="totalPages"
                :value="parseInt(pagination.current)"
                @input="setCurrentPage"
              />
            </div>
            <div
              v-if="!showCalendar"
              class="col-12 col-lg-4 text-center text-lg-right"
            >
              <PageSize
                :value="parseInt(pagination.per_page)"
                @input="setPerPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CancelEventModal from 'components/pages/admin/events/partials/CancelEventModal'
import DisplaysAlerts from "mixins/DisplaysAlerts";
import ListingMixin from "mixins/Listing";
import DetailsPane from "components/listings/DetailsPane";
import ListCard from "components/partials/Events/ListCard";
import ListMenu from "components/partials/Events/ListMenu";
import EventDetailsPane from "components/partials/Events/DetailsPane";

export default {
    mixins: [DisplaysAlerts, ListingMixin],
    components: {
        DetailsPane,
        ListCard,
        ListMenu,
        EventDetailsPane,
    },
    props: {
        toggles: {type: Array, default: () => []},
        cancellationTypes: {type: Array, default: () => []}
    },
    data() {
        return {
            eventStatus: null,
            month: this.$moment().format("YYYY-MM"),
            day: null,
            showCalendar: true,
            trainedByMe: false,
            isLocalContact: false,
        };
    },
    computed: {
        events() {
            return this.items.filter((item) => {
                if (this.day) {
                    var start = parseInt(this.$moment(item.start_date).format("D"), 10);
                    var end = parseInt(this.$moment(item.end_date).format("D"), 10);
                    return this.day >= start && this.day <= end;
                }
                return true;
            });
        },
        calendarDayClasses() {
            var classnames = {};
            for (var d = 1; d <= this.lastDayOfTheMonth; d++) {
                var date = this.$moment(this.month).date(d).format("YYYY-MM-DD");
                var events = this.items.filter((item) => {
                    var start = parseInt(this.$moment(item.start_date).format("D"), 10);
                    var end = parseInt(this.$moment(item.end_date).format("D"), 10);
                    return d >= start && d <= end;
                });
                if (events.length > 0) {
                    classnames[date] = "has-events";
                }
            }
            return classnames;
        },
        lastDayOfTheMonth() {
            return parseInt(this.$moment(this.month).endOf("month").format("D"), 10);
        },
    },
    watch: {
        keywords: function (val) {
            if (this.showCalendar) {
                this.hideCalendar();
            }
        },
        filters: function (val) {
            if (this.showCalendar) {
                this.hideCalendar();
            }
        },
        month(val) {
            this.day = null;
            this.refresh();
        },
        eventStatus(val) {
            this.replaceFilter("status", val);
        },
        trainedByMe(val) {
            if (!! val) {
                this.replaceFilter("trained_by_me", 1);
            } else {
                this.clearFilter("trained_by_me");
            }
        },
        isLocalContact(val) {
            if (!! val) {
                this.replaceFilter("is_local_contact", 1);
            } else {
                this.clearFilter("is_local_contact");
            }
        },
    },
    methods: {
        cancel(item) {
            this.$modal(CancelEventModal, {
                event: item,
                cancellationTypes: this.cancellationTypes,
            })
                .then(() => {
                    this.listEvents.$emit("refresh");
                    this.listEvents.$emit(
                        "show-alert",
                        "success",
                        `"${item.name}" has been canceled.`,
                        true
                    );
                })
                .catch();
        },
        customParams() {
            return this.showCalendar ? {month: this.month} : {};
        },
        handleMenuAction(action, item) {
            if (action === 'cancel') {
                this.cancel(item);
            }
        },
        prevMonth() {
            var prev = this.$moment(this.month)
                .subtract(1, "months")
                .format("YYYY-MM");
            this.month = prev;
        },
        nextMonth() {
            var next = this.$moment(this.month).add(1, "months").format("YYYY-MM");
            this.month = next;
        },
        viewByDay(day) {
            this.day = day;
        },
        viewCalendar() {
            this.pagination = {
                current: 1,
                per_page: 999,
            };
            this.removeAllCalendarFilters();
            this.$nextTick(() => {
                this.showCalendar = true;
            });
        },
        hideCalendar() {
            this.pagination = {
                current: 1,
                per_page: 10,
            };
            this.$nextTick(() => {
                this.showCalendar = false;
            });
        },
        removeAllCalendarFilters() {
            this.eventStatus = null;
            this.isLocalContact = false;
            this.trainedByMe = false;
            this.removeAllFilters();
        },
    },
    mounted() {
        if (_.has(this.filters, "status")) {
            this.eventStatus = _.head(this.filters.status);
        }
        if (_.has(this.filters, "trained_by_me")) {
            this.trainedByMe = !! _.head(this.filters.trained_by_me);
        }
        if (_.has(this.filters, "is_local_contact")) {
            this.isLocalContact = !! _.head(this.filters.is_local_contact);
        }
        this.listEvents.$on("menu-action", this.handleMenuAction);
        this.listEvents.$on("show-details-pane", this.setSelectedItem);
        this.listEvents.$on("show-alert", this.showAlert);
        this.pagination.per_page = this.showCalendar ? 999 : 10;
    },
};
</script>